import { useState, useCallback } from 'react';
import { GRID_SIZE } from '../constants/gameConstants';
import { addCoinsToState, copyCoin } from '../utils/tokenUtils';
import { syncActiveMapState } from '../utils/gameStateManager';

/**
 * Tokens / coins: CRUD, initiative sync, and panel-to-grid drag.
 * @param {object} params.tokens - coins, initiative, setSelectedCoinId + setters
 * @param {object} params.sync - roomId, viewingMapId, currentMapId, socketHook
 * @param {object} params.gridSize - gridWidth, gridHeight
 * @param {object} params.refs - gameContainerRef, gameGridRef
 */
export function useTokenSession({ tokens, sync, gridSize, refs }) {
  const {
    coins,
    setCoins,
    initiativeOrder,
    setInitiativeOrder,
    setSelectedCoinId,
  } = tokens;
  const { roomId, viewingMapId, currentMapId, socketHook } = sync;
  const { gridWidth, gridHeight } = gridSize;
  const { gameContainerRef, gameGridRef } = refs;

  const syncWithSocket = useCallback(
    (updatedCoins, updatedOrder) => {
      syncActiveMapState({
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        patch: {
          coins: updatedCoins,
          initiativeOrder: updatedOrder,
        },
      });
    },
    [roomId, viewingMapId, currentMapId, socketHook]
  );

  const updateCoinById = useCallback(
    (coinId, updateFn) => {
      const updatedCoins = coins.map((c) => (c.id === coinId ? updateFn(c) : c));
      setCoins(updatedCoins);

      const updatedOrder = initiativeOrder.map((c) =>
        c.id === coinId ? updateFn(c) : c
      );
      setInitiativeOrder(updatedOrder);

      syncWithSocket(updatedCoins, updatedOrder);
      return { updatedCoins, updatedOrder };
    },
    [coins, initiativeOrder, setCoins, setInitiativeOrder, syncWithSocket]
  );

  const handleEdit = useCallback(
    (coinId, newName, newColor, isLarge, isEnemy, armorClass, level, isPc) => {
      updateCoinById(coinId, (c) => {
        const next = {
          ...c,
          name: newName,
          color: newColor,
          isLarge,
          isEnemy,
          armorClass,
        };
        if (isEnemy) {
          delete next.level;
          delete next.isPc;
        } else {
          if (level != null && level >= 1 && level <= 20) {
            next.level = level;
          } else {
            delete next.level;
          }
          if (typeof isPc === 'boolean') {
            next.isPc = isPc;
          }
        }
        return next;
      });
    },
    [updateCoinById]
  );

  const handleEditHealth = useCallback(
    (coinId, newHealth) => {
      updateCoinById(coinId, (c) => {
        const updatedCoin = {
          ...c,
          maxHealth: c.maxHealth ?? newHealth,
          currentHealth: newHealth,
        };

        if (newHealth <= 0) {
          const currentStatuses = c.statuses || [];
          const statusToAdd = c.isEnemy ? 'Death' : 'Bleeding Out';
          if (!currentStatuses.includes(statusToAdd)) {
            updatedCoin.statuses = [...currentStatuses, statusToAdd];
          }
        } else if (c.statuses) {
          const statusToRemove = c.isEnemy ? 'Death' : 'Bleeding Out';
          if (c.statuses.includes(statusToRemove)) {
            updatedCoin.statuses = c.statuses.filter((s) => s !== statusToRemove);
          }
        }

        return updatedCoin;
      });
    },
    [updateCoinById]
  );

  const handleEditStatus = useCallback(
    (coinId, newStatuses) => {
      updateCoinById(coinId, (c) => ({ ...c, statuses: newStatuses }));
    },
    [updateCoinById]
  );

  const handleToggleHidden = useCallback(
    (coinId) => {
      updateCoinById(coinId, (c) => ({ ...c, hidden: !c.hidden }));
    },
    [updateCoinById]
  );

  const handleRemoveFromGrid = useCallback(
    (coinId) => {
      updateCoinById(coinId, (c) => ({
        ...c,
        removed: true,
        x: undefined,
        y: undefined,
      }));
      setSelectedCoinId(null);
    },
    [updateCoinById, setSelectedCoinId]
  );

  const handleDragEnd = useCallback(
    (e, coin, isDelete, newPos) => {
      if (isDelete) {
        const updatedCoins = coins.filter((c) => c.id !== coin.id);
        setCoins(updatedCoins);

        const updatedOrder = initiativeOrder.filter((c) => c.id !== coin.id);
        setInitiativeOrder(updatedOrder);

        syncWithSocket(updatedCoins, updatedOrder);
        setSelectedCoinId((prev) => (prev === coin.id ? null : prev));
        return;
      }

      if (newPos) {
        const updatedCoins = coins.map((c) =>
          c.id === coin.id ? { ...c, x: newPos.x, y: newPos.y } : c
        );
        setCoins(updatedCoins);

        syncActiveMapState({
          roomId,
          viewingMapId,
          currentMapId,
          socketHook,
          patch: { coins: updatedCoins },
        });
      }
    },
    [
      coins,
      initiativeOrder,
      setCoins,
      setInitiativeOrder,
      setSelectedCoinId,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      syncWithSocket,
    ]
  );

  const handleCoinCopy = useCallback(
    (coin) => {
      const newCoin = copyCoin(coin);

      addCoinsToState({
        newCoins: [newCoin],
        currentCoins: coins,
        currentInitiativeOrder: initiativeOrder,
        roomId,
        viewingMapId,
        currentMapId,
        socketHook,
        setCoins,
        setInitiativeOrder,
      });
    },
    [
      coins,
      initiativeOrder,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      setCoins,
      setInitiativeOrder,
    ]
  );

  const [draggingToken, setDraggingToken] = useState(null);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });

  const handleTokenDragStart = useCallback((coin, x, y) => {
    setDraggingToken(coin);
    setDragPosition({ x, y });
  }, []);

  const isPositionOccupied = useCallback((x, y, isLarge, coinsList, excludeCoinId) => {
    return coinsList.some((c) => {
      if (c.id === excludeCoinId) return false;
      if (c.removed) return false;

      if (isLarge) {
        if (c.isLarge) {
          return (
            c.x <= x + GRID_SIZE &&
            c.x + GRID_SIZE * 2 > x &&
            c.y <= y + GRID_SIZE &&
            c.y + GRID_SIZE * 2 > y
          );
        }
        return (
          c.x >= x &&
          c.x < x + GRID_SIZE * 2 &&
          c.y >= y &&
          c.y < y + GRID_SIZE * 2
        );
      }
      if (c.isLarge) {
        return x >= c.x && x < c.x + GRID_SIZE * 2 && y >= c.y && y < c.y + GRID_SIZE * 2;
      }
      return c.x === x && c.y === y;
    });
  }, []);

  const findClosestAvailablePosition = useCallback(
    (targetX, targetY, gridW, gridH, isLarge, coinsList, excludeCoinId) => {
      const targetGridX = Math.round(targetX / GRID_SIZE) * GRID_SIZE;
      const targetGridY = Math.round(targetY / GRID_SIZE) * GRID_SIZE;

      const clampedTargetX = Math.max(
        0,
        Math.min(targetGridX, (gridW - (isLarge ? 2 : 1)) * GRID_SIZE)
      );
      const clampedTargetY = Math.max(
        0,
        Math.min(targetGridY, (gridH - (isLarge ? 2 : 1)) * GRID_SIZE)
      );

      if (!isPositionOccupied(clampedTargetX, clampedTargetY, isLarge, coinsList, excludeCoinId)) {
        return { x: clampedTargetX, y: clampedTargetY };
      }

      const maxSearchRadius = Math.max(gridW, gridH);

      for (let radius = 1; radius <= maxSearchRadius; radius++) {
        let closestPos = null;
        let closestDist = Infinity;

        for (let di = -radius; di <= radius; di++) {
          for (let dj = -radius; dj <= radius; dj++) {
            if (Math.abs(di) !== radius && Math.abs(dj) !== radius) continue;

            const gridI = Math.round(clampedTargetX / GRID_SIZE) + di;
            const gridJ = Math.round(clampedTargetY / GRID_SIZE) + dj;

            if (gridI < 0 || gridI >= gridW - (isLarge ? 1 : 0)) continue;
            if (gridJ < 0 || gridJ >= gridH - (isLarge ? 1 : 0)) continue;

            const px = gridI * GRID_SIZE;
            const py = gridJ * GRID_SIZE;

            if (!isPositionOccupied(px, py, isLarge, coinsList, excludeCoinId)) {
              const dist = Math.sqrt(
                Math.pow(px - clampedTargetX, 2) + Math.pow(py - clampedTargetY, 2)
              );

              if (dist < closestDist) {
                closestDist = dist;
                closestPos = { x: px, y: py };
              }
            }
          }
        }

        if (closestPos) {
          return closestPos;
        }
      }

      return { x: 0, y: 0 };
    },
    [isPositionOccupied]
  );

  const handleTokenDragEnd = useCallback(
    (coin, clientX, clientY) => {
      if (!gameContainerRef?.current || !gameGridRef?.current) {
        setDraggingToken(null);
        return;
      }

      const containerRect = gameContainerRef.current.getBoundingClientRect();

      if (
        clientX >= containerRect.left &&
        clientX <= containerRect.right &&
        clientY >= containerRect.top &&
        clientY <= containerRect.bottom
      ) {
        const worldPos = gameGridRef.current.screenToWorld(clientX, clientY);

        if (worldPos) {
          const coinSize = coin.isLarge ? GRID_SIZE * 2 : GRID_SIZE;
          const topLeftX = worldPos.x - coinSize / 2;
          const topLeftY = worldPos.y - coinSize / 2;

          const closestPosition = findClosestAvailablePosition(
            topLeftX,
            topLeftY,
            gridWidth,
            gridHeight,
            coin.isLarge,
            coins,
            coin.id
          );

          if (closestPosition) {
            const finalCoins = coins.map((c) =>
              c.id === coin.id
                ? { ...c, x: closestPosition.x, y: closestPosition.y, removed: false }
                : c
            );
            setCoins(finalCoins);

            setInitiativeOrder((prevOrder) =>
              prevOrder.map((c) =>
                c.id === coin.id
                  ? { ...c, x: closestPosition.x, y: closestPosition.y, removed: false }
                  : c
              )
            );

            const updatedOrder = initiativeOrder.map((c) =>
              c.id === coin.id
                ? { ...c, x: closestPosition.x, y: closestPosition.y, removed: false }
                : c
            );
            syncActiveMapState({
              roomId,
              viewingMapId,
              currentMapId,
              socketHook,
              patch: {
                coins: finalCoins,
                initiativeOrder: updatedOrder,
              },
            });
          }
        }
      }

      setDraggingToken(null);
    },
    [
      coins,
      gridWidth,
      gridHeight,
      roomId,
      viewingMapId,
      currentMapId,
      socketHook,
      initiativeOrder,
      findClosestAvailablePosition,
      gameContainerRef,
      gameGridRef,
      setCoins,
      setInitiativeOrder,
    ]
  );

  return {
    handleEdit,
    handleEditHealth,
    handleEditStatus,
    handleToggleHidden,
    handleRemoveFromGrid,
    handleDragEnd,
    handleCoinCopy,
    draggingToken,
    dragPosition,
    setDragPosition,
    handleTokenDragStart,
    handleTokenDragEnd,
  };
}
